import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { RequireAuth, RedirectIfAuthed } from "@/components/RouteGuards";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AppShell from "@/components/shell/AppShell";
import DashboardHome from "@/pages/app/DashboardHome";
import Settings from "@/pages/app/Settings";
import ProductsList from "@/pages/app/products/ProductsList";
import ProductForm from "@/pages/app/products/ProductForm";
import CommunityExplore from "@/pages/app/CommunityExplore";
import PublicProduct from "@/pages/PublicProduct";
import CampaignsList from "@/pages/app/campaigns/CampaignsList";
import CampaignNew from "@/pages/app/campaigns/CampaignNew";
import CampaignDetail from "@/pages/app/campaigns/CampaignDetail";
import CampaignSubmit from "@/pages/CampaignSubmit";
import MyFeedback from "@/pages/app/MyFeedback";
import AdminModeration from "@/pages/app/admin/AdminModeration";
import AdminUsers from "@/pages/app/admin/AdminUsers";
import ReportsIndex from "@/pages/app/reports/ReportsIndex";
import Reputation from "@/pages/app/Reputation";
import Ambassadors from "@/pages/app/Ambassadors";
import AdminAmbassadors from "@/pages/app/admin/AdminAmbassadors";
import AdminReferrals from "@/pages/app/admin/AdminReferrals";
import Referrals from "@/pages/app/Referrals";
import Billing from "@/pages/app/Billing";
import Payouts from "@/pages/app/Payouts";
import AdminPayouts from "@/pages/app/admin/AdminPayouts";
import MockCheckout from "@/pages/MockCheckout";
import MockConnectOnboarding from "@/pages/MockConnectOnboarding";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AmbassadorPublicProfile from "@/pages/AmbassadorPublicProfile";
import HowDoesItWork from "@/pages/app/HowDoesItWork";
import { Privacy, Terms, Cookies } from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";
import NotFound from "@/pages/NotFound";

function ComingSoon({ title }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Coming soon</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl mt-2 text-[#0B1F3A]">{title}</h1>
      <p className="mt-3 text-[#0B1F3A]/70 max-w-xl">This module ships in a later build cycle. Cycle C1 delivered the design system, app shell, and auth foundation.</p>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <CookieBanner />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
          <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/cookies" element={<Cookies />} />
          <Route path="/p/:slug" element={<PublicProduct />} />
          <Route path="/campaign/:id" element={<CampaignSubmit />} />
          <Route path="/a/:slug" element={<AmbassadorPublicProfile />} />
          <Route path="/mock-checkout/:sessionId" element={<MockCheckout />} />
          <Route path="/mock-connect/:accountId" element={<MockConnectOnboarding />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<DashboardHome />} />
            <Route path="settings" element={<Settings />} />
            <Route path="products" element={<ProductsList />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id" element={<ProductForm />} />
            <Route path="explore" element={<CommunityExplore />} />
            <Route path="campaigns" element={<CampaignsList />} />
            <Route path="campaigns/new" element={<CampaignNew />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="my-feedback" element={<MyFeedback />} />
            <Route path="admin/moderation" element={<AdminModeration />} />
            <Route path="admin/users" element={<AdminUsers />} />
            <Route path="reports" element={<ReportsIndex />} />
            <Route path="reputation" element={<Reputation />} />
            {/* Placeholders for later cycles */}
            <Route path="ambassadors" element={<Ambassadors />} />
            <Route path="admin/ambassadors" element={<AdminAmbassadors />} />
            <Route path="admin/referrals" element={<AdminReferrals />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="billing" element={<Billing />} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="earnings" element={<Payouts />} />
            <Route path="admin/payouts" element={<AdminPayouts />} />
            <Route path="how-it-works" element={<HowDoesItWork />} />
            <Route path="profile" element={<ComingSoon title="Public profile" />} />
            <Route path="admin/*" element={<ComingSoon title="Admin console" />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
